import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { EquifaxReportView } from "@/components/EquifaxReportView"
import { apiFetch } from "@prescreen/lib/api-client"

interface HardpullReportData {
  _id: string;
  hardpullRequestId: string;
  prescreenRequestId: string;
  bureau: string;
  reportData: {
    creditScore?: number;
    rawResponse?: unknown;
    timestamp: string;
    isSimulated: boolean;
  };
  consumerName: string;
  createdAt: string;
}

export default function HardpullReportPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const reportId = id as string

  const [hardpullReport, setHardpullReport] = useState<HardpullReportData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  useEffect(() => {
    const loadHardpullReport = async () => {
      if (!reportId) {
        setError("Missing report ID")
        setIsLoading(false)
        return
      }

      try {
        const response = await apiFetch(`/api/hardpull/reports/${reportId}`)
        const json = await response.json()

        if (json.success) {
          setHardpullReport(json.data.hardpullReport)
        } else {
          setError(json.error || "Failed to load hardpull report")
        }
      } catch {
        setError("Network error. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    loadHardpullReport()
  }, [reportId])

  const handleGeneratePdf = async () => {
    if (!hardpullReport?._id) return

    setIsGeneratingPdf(true)
    try {
      const response = await apiFetch(
        `/api/hardpull/reports/${hardpullReport._id}/pdf`
      )

      if (!response.ok) {
        throw new Error("Failed to generate PDF")
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const newWindow = window.open(url, "_blank")

      if (newWindow) {
        setTimeout(() => {
          window.URL.revokeObjectURL(url)
        }, 100)
      } else {
        const a = document.createElement("a")
        a.href = url
        a.download = `hardpull-report-${hardpullReport._id}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error("Error generating PDF:", err)
      setError(err instanceof Error ? err.message : "Failed to generate PDF")
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const getBureauLabel = (bureau: string): string => {
    const labels: Record<string, string> = {
      equifax: "Equifax",
      experian: "Experian",
      transunion: "TransUnion",
    }
    return labels[bureau] || bureau
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading hard pull report...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !hardpullReport) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-600">
                Error Loading Hard Pull Report
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertDescription>
                  {error || "Hard pull report not found"}
                </AlertDescription>
              </Alert>
              <div className="mt-4">
                <Button onClick={() => navigate("/reports")} variant="outline">
                  Back to Reports
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const rawReport = hardpullReport.reportData.rawResponse as
    | {
        consumers?: { equifaxUSConsumerCreditReport?: unknown[] };
      }
    | undefined;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button onClick={() => navigate("/reports")} variant="outline">
              ← Back to Reports
            </Button>
            <Button
              onClick={handleGeneratePdf}
              disabled={isGeneratingPdf || !hardpullReport?._id}
              variant="default"
            >
              {isGeneratingPdf ? "Generating PDF..." : "Generate PDF"}
            </Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Hard Pull Report Details
          </h1>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Report Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Consumer Name
                  </h4>
                  <p className="text-sm">{hardpullReport.consumerName}</p>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Bureau
                  </h4>
                  <Badge variant="outline">
                    {getBureauLabel(hardpullReport.bureau)}
                  </Badge>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Credit Score
                  </h4>
                  <span className="text-2xl font-bold text-blue-600">
                    {hardpullReport.reportData.creditScore || "N/A"}
                  </span>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Report ID
                  </h4>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {hardpullReport._id}
                  </code>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Prequalification Request ID
                  </h4>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">
                    {hardpullReport.prescreenRequestId}
                  </code>
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-700 mb-2">
                    Timestamp
                  </h4>
                  <p className="text-sm">
                    {new Date(
                      hardpullReport.reportData.timestamp
                    ).toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {rawReport?.consumers?.equifaxUSConsumerCreditReport ? (
            <EquifaxReportView
              rawReport={rawReport}
              reportTitle="Hard Pull Credit Report"
              bureau={hardpullReport.bureau}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Raw Report Data</CardTitle>
                <CardDescription>
                  Unable to parse report in standard format
                </CardDescription>
              </CardHeader>
              <CardContent>
                <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-96">
                  {JSON.stringify(
                    hardpullReport.reportData.rawResponse,
                    null,
                    2
                  )}
                </pre>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}

